/* eslint-disable react-refresh/only-export-components */
import { useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { Spin } from 'antd'
import LoginPage from './login/page'
import LogoutPage from './logout/page'
import ForgotPasswordPage from './forgot-password/page'
import SignupPage from './signup/page'
import { SIGNUP_LANDING_STORAGE_KEY } from './signup/useSignupFlow'
import ConfirmSignupPage from './confirm-signup/page'
import ConfirmLoginPage from './confirm-login/page'
import ResetPasswordPage from './reset-password/page'
import SignupXeroPage from './signup-xero/page'
import MarkReadyBasPage from './mark-ready-bas/page'
import QuickBooksCallbackPage from '@/features/platform/QuickBooksCallbackPage'
import MyobCallbackPage from '@/features/platform/MyobCallbackPage'
import BasSignupUnavailablePage from './bas-signup-unavailable/page'
import { useAuthorization } from '@/contexts/AuthorizationContext'
import { resolveHomePath } from '@/config/nav'
import { hasProtectedPathAccess } from '@/routes/protected'
import { useAuthSession } from '@/lib/session'

export function HomeRedirect() {
  const session = useAuthSession()
  const { permissions, isReady } = useAuthorization()

  const [landingPath] = useState(() => sessionStorage.getItem(SIGNUP_LANDING_STORAGE_KEY))

  // Dropped on mount, not while reading: StrictMode invokes the initializer
  // twice, and clearing in there loses the landing to the discarded call. A
  // leftover would otherwise hijack a later trip through the home redirect.
  useEffect(() => {
    sessionStorage.removeItem(SIGNUP_LANDING_STORAGE_KEY)
  }, [])

  if (!isReady && session) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Spin size="large" />
      </div>
    )
  }

  // AdvicePrep signup asks to land on AdvicePrep itself. Checked rather than
  // navigated to directly: an account whose grant or env flag says otherwise
  // would be bounced to /unauthorized straight after paying.
  if (landingPath && session && hasProtectedPathAccess(landingPath, session, permissions)) {
    return <Navigate to={landingPath} replace />
  }

  return <Navigate to={resolveHomePath(session, permissions)} replace />
}

function UpdateEmailRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={id ? `/login?action=update-email&id=${encodeURIComponent(id)}` : '/login'} replace />
}

export const authRouteConfig = [
  { path: '/home', element: <Navigate to="/" replace /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/update-email/:id', element: <UpdateEmailRedirect /> },
  { path: '/signup', element: <SignupPage /> },
  { path: '/signup/xero', element: <SignupXeroPage /> },
  { path: '/adviceprep-client-signup', element: <SignupPage /> },
  // The pre-rename URL is public and already circulating.
  { path: '/adviseprep-client-signup', element: <Navigate to="/adviceprep-client-signup" replace /> },
  { path: '/wfm-client-signup', element: <SignupPage /> },
  { path: '/wfm-client-signup/importData', element: <SignupPage /> },
  { path: '/xero-client-signup', element: <SignupPage /> },
  { path: '/karbon-client-signup', element: <SignupPage /> },
  { path: '/fyi-client-signup', element: <SignupPage /> },
  { path: '/bas-client-signup', element: <BasSignupUnavailablePage /> },
  { path: '/client-signup', element: <Navigate to="/wfm-client-signup" replace /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/logout', element: <LogoutPage /> },
  { path: '/signout', element: <LogoutPage /> },
  { path: '/confirm-signup/:id', element: <ConfirmSignupPage /> },
  { path: '/usersignup-confirmation/:id', element: <ConfirmSignupPage /> },
  { path: '/confirm-login/:id', element: <ConfirmLoginPage /> },
  { path: '/usersignin-confirmation/:id', element: <ConfirmLoginPage /> },
  { path: '/reset-password/:id', element: <ResetPasswordPage /> },
  { path: '/mark-ready-bas', element: <MarkReadyBasPage /> },
  { path: '/quickbooks', element: <QuickBooksCallbackPage /> },
  { path: '/myob', element: <MyobCallbackPage /> },
]
